import { useEffect, type ReactNode } from 'react';

export interface ModalProps {
  title: string;
  onClose(): void;
  children: ReactNode;
  wide?: boolean;
  /**
   * True while a blocking async operation started from inside this modal is
   * in flight (saving, confirming a destructive action, etc.). While true,
   * neither ESC nor a backdrop click closes the modal — the operation has to
   * resolve first. Every caller that tracks a `saving`/`busy` boolean should
   * pass it here; callers that don't pass it keep the old always-closable
   * behavior.
   */
  busy?: boolean;
}

export function Modal({ title, onClose, children, wide, busy }: ModalProps) {
  // Closing on ESC belongs here, once, rather than in every caller: every
  // page that opens a Modal inherits it for free. See
  // docs/decisions/2026-07-31-modal-esc-and-busy-guard.md.
  useEffect(() => {
    if (busy) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [busy, onClose]);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (busy) return;
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={`modal${wide ? ' modal--wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}
